import {CSSProperties, forwardRef, useImperativeHandle, useRef} from 'react'
import _ from 'lodash'
import {Popup} from 'semantic-ui-react'
import {hslToHex} from '@/utils/colors'
import {IEvent, IUserLabelsMap, IUserLabelsTrack} from '@/features/labeling'
import {secondsToTimeString, useConfig} from '@/features/videolab'

export interface IEventButtonsProps {
  events: IEvent[]
  userLabels?: IUserLabelsMap
  selectedEvent?: IEvent
  setSelectedEvent: (e: IEvent) => void
}

export interface IEventButtonsRef {
  scrollTo: (event: IEvent) => void
}

export const EventButtons = forwardRef<IEventButtonsRef, IEventButtonsProps>((props, ref) => {
  const {events, userLabels, selectedEvent, setSelectedEvent} = props

  const {denoise, denoiseThreshold, formatTime} = useConfig()

  // const {catalogStats} = useContext(CatalogContext);
  // const {fieldTypes} = catalogStats;

  const eventButtonsRef = useRef<{[key: string]: HTMLDivElement}>({})

  useImperativeHandle(ref, () => ({
    scrollTo,
  }))

  function scrollTo(event: IEvent) {
    const k = _eventKey(event)
    const elem = eventButtonsRef.current[k]
    if (!elem) return
    elem.scrollIntoView({behavior: 'smooth', block: 'center'})
  }

  function computeStyle(event: IEvent, track?: IUserLabelsTrack): CSSProperties {
    const {start, hue} = event
    const res: CSSProperties = {}
    if (_.has(track, start)) res.opacity = 0.5
    if (_.isNumber(hue)) res.backgroundColor = hslToHex(hue)
    if (event === selectedEvent) {
      res.border = '2px solid var(--text-color)'
      res.animation = 'pulse 0.5s linear alternate infinite'
    }
    return res
  }

  let prev: number | undefined = undefined

  function _prettyTimestamp(time: number) {
    const res = time.toFixed(2)
    if (_.endsWith(res, '.00')) return res.substring(0, res.length - 3)
    return res
  }

  function _eventKey(event: IEvent): string {
    return `${event.type}_${event.start}`
  }

  return (
    <div className='event-buttons'>
      {_.chain(events)
        .groupBy((e) => e.group || '')
        .toPairs()
        .map(([group, groupedEvents]) => {
          return (
            <div
              key={group}
              className='group'
            >
              {group ?
                <div className='header'>{group}</div>
              : null}
              {_.chain(groupedEvents)
                .map((e, j) => {
                  const {type, start, end, text, hidden} = e
                  if (hidden) return
                  if (denoise && denoiseThreshold) {
                    const _keep = prev === undefined || start - prev > denoiseThreshold
                    prev = start
                    if (!_keep) return
                  }
                  if (!type) return
                  if (!_.isNumber(start)) return
                  // let content = `${e.type}: ${start}`
                  let content: string | undefined
                  if (start === -1 && end === -1) content = '(everything)'
                  else {
                    const _timeComponents: number[] = [start]
                    if (_.isNumber(end)) _timeComponents.push(end)
                    content = _.chain(_timeComponents)
                      .map((v) => (formatTime ? secondsToTimeString(v, true) : _prettyTimestamp(v)))
                      .join(' - ')
                      .value()
                  }
                  if (!content) return
                  const labelTrack = _.get(userLabels, type)
                  const res = (
                    <div
                      className='event'
                      ref={(r) => {
                        if (!e.type) return
                        const k = _eventKey(e)
                        if (!r) delete eventButtonsRef.current[k]
                        else eventButtonsRef.current[k] = r
                      }}
                      key={j}
                      onClick={() => {
                        if (!setSelectedEvent) return
                        setSelectedEvent(e)
                      }}
                      style={computeStyle(e, labelTrack)}
                      children={content}
                    />
                  )
                  if (text)
                    return (
                      <Popup
                        key={res.key}
                        size='tiny'
                        trigger={res}
                        content={text}
                      />
                    )
                  return res
                })
                .value()}
            </div>
          )
        })
        .compact()
        .flatten()
        .value()}
    </div>
  )
})
